const RESIZE_DEBOUNCE_MS = 150;
const NAV_READY_FALLBACK_MS = 400;
const DESKTOP_NAV_MEDIA = '(min-width: 1024px)';

class ProductCrossSell extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.contents = [];
    this.tabListeners = [];
    this.handleRootClick = this.handleRootClick.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('.product-cross-sell__tab');
    this.contents = this.querySelectorAll('.product-cross-sell__tab-content');
    this.init();
    this.bindEvents();
    this.initVisibilityObserver();
    window.addEventListener('resize', this.handleWindowResize);
  }

  disconnectedCallback() {
    this.tabListeners.forEach(({ tab, onClick, onKeydown }) => {
      tab.removeEventListener('click', onClick);
      tab.removeEventListener('keydown', onKeydown);
    });
    this.tabListeners = [];

    this.removeEventListener('click', this.handleRootClick);

    window.removeEventListener('resize', this.handleWindowResize);
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = null;
    }

    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    this.wasIntersecting = false;
  }

  initVisibilityObserver() {
    if (typeof IntersectionObserver === 'undefined') return;

    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }

    this.wasIntersecting = false;
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this) return;

          const visible = entry.isIntersecting && entry.intersectionRatio > 0;

          if (visible && !this.wasIntersecting) {
            this.wasIntersecting = true;
            requestAnimationFrame(() => this.refreshActiveFlickity());
          } else if (!visible) {
            this.wasIntersecting = false;
          }
        });
      },
      { threshold: [0, 0.01] },
    );

    this.visibilityObserver.observe(this);
  }

  handleWindowResize() {
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }
    this.resizeDebounceTimer = setTimeout(() => {
      this.resizeDebounceTimer = null;
      this.refreshActiveFlickity();
    }, RESIZE_DEBOUNCE_MS);
  }

  refreshActiveFlickity() {
    const activeSlider = this.getActiveSlider();
    if (!activeSlider?.flickityInstance) return;

    const flkty = activeSlider.flickityInstance;
    flkty.reloadCells();
    flkty.resize();
    this.scheduleNavigationUpdate(flkty);
  }

  init() {
    const activeContent = this.querySelector('.product-cross-sell__tab-content.is-active');
    if (activeContent) {
      this.initSlider(activeContent);
    }
  }

  bindEvents() {
    this.tabListeners = [];

    this.tabs.forEach((tab) => {
      const onClick = () => {
        const target = tab.dataset.tab;
        this.switchTab(target);
      };
      const onKeydown = (event) => {
        this.handleTabKeydown(event, tab);
      };

      tab.addEventListener('click', onClick);
      tab.addEventListener('keydown', onKeydown);
      this.tabListeners.push({ tab, onClick, onKeydown });
    });

    this.addEventListener('click', this.handleRootClick);
  }

  getActiveSlider() {
    return this.querySelector('.product-cross-sell__tab-content.is-active .product-cross-sell__slider');
  }

  isDesktopNav() {
    return window.matchMedia(DESKTOP_NAV_MEDIA).matches;
  }

  getNavContext(flkty) {
    const slider = flkty.element;
    const region = slider.closest('.product-cross-sell__slider-region');
    const tabsWrapper = this.querySelector('.product-cross-sell__tabs-wrapper');
    const tabsNav = this.querySelector('.product-cross-sell__navigation--tabs');
    const sliderNav = region?.querySelector('.product-cross-sell__navigation--slider');

    const prevBtns = [];
    const nextBtns = [];

    [tabsNav, sliderNav].forEach((nav) => {
      if (!nav) return;
      const prev = nav.querySelector('.product-cross-sell__nav-btn--prev');
      const next = nav.querySelector('.product-cross-sell__nav-btn--next');
      if (prev) prevBtns.push(prev);
      if (next) nextBtns.push(next);
    });

    return { region, tabsWrapper, tabsNav, sliderNav, prevBtns, nextBtns };
  }

  setNavButtonStates(prevBtns, nextBtns, atStart, atEnd) {
    prevBtns.forEach((btn) => {
      btn.disabled = atStart;
      btn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    });

    nextBtns.forEach((btn) => {
      btn.disabled = atEnd;
      btn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    });
  }

  hideSliderNavigation() {
    this.querySelectorAll('.product-cross-sell__slider-region').forEach((region) => {
      region.classList.remove('is-nav-visible');
      region.querySelector('.product-cross-sell__navigation--slider')?.setAttribute('aria-hidden', 'true');
    });
  }

  hideAllNavigation() {
    this.hideSliderNavigation();
  }

  isNavRevealed(flkty) {
    const { region } = this.getNavContext(flkty);
    return Boolean(region?.classList.contains('is-nav-visible'));
  }

  updateNavigation(flkty, { reveal = false } = {}) {
    const { region, tabsNav, sliderNav, prevBtns, nextBtns } = this.getNavContext(flkty);
    const isDesktop = this.isDesktopNav();
    const lastIndex = Math.max(flkty.slides.length - 1, 0);
    const needsNav = lastIndex > 0;

    const atStart = flkty.selectedIndex <= 0;
    const atEnd = flkty.selectedIndex >= lastIndex;
    this.setNavButtonStates(prevBtns, nextBtns, atStart || !needsNav, atEnd || !needsNav);

    if (isDesktop) {
      tabsNav?.setAttribute('aria-hidden', 'false');
      region?.classList.remove('is-nav-visible');
      sliderNav?.setAttribute('aria-hidden', 'true');
      return;
    }

    if (!needsNav || (atStart && atEnd)) {
      region?.classList.remove('is-nav-visible');
      sliderNav?.setAttribute('aria-hidden', 'true');
      return;
    }

    if (reveal) {
      region?.classList.add('is-nav-visible');
      sliderNav?.setAttribute('aria-hidden', 'false');
    }
  }

  scheduleNavigationUpdate(flkty) {
    const slider = flkty.element;

    const runUpdate = () => {
      this.equalizeSlideHeights(slider);
      flkty.resize();
      const needsNav = flkty.slides.length > 1;
      this.updateNavigation(flkty, { reveal: needsNav });
    };

    const images = slider.querySelectorAll('img');
    const pending = Array.from(images).filter((img) => !img.complete);

    if (pending.length === 0) {
      requestAnimationFrame(() => requestAnimationFrame(runUpdate));
      return;
    }

    let settled = 0;
    const onImageSettled = () => {
      settled += 1;
      if (settled >= pending.length) {
        requestAnimationFrame(() => requestAnimationFrame(runUpdate));
      }
    };

    pending.forEach((img) => {
      img.addEventListener('load', onImageSettled, { once: true });
      img.addEventListener('error', onImageSettled, { once: true });
    });

    setTimeout(runUpdate, NAV_READY_FALLBACK_MS);
  }

  handleRootClick(e) {
    const target = e.target;

    if (target.closest('.product-cross-sell__nav-btn--prev')) {
      e.preventDefault();
      const activeSlider = this.getActiveSlider();
      activeSlider?.flickityInstance?.previous();
      return;
    }

    if (target.closest('.product-cross-sell__nav-btn--next')) {
      e.preventDefault();
      const activeSlider = this.getActiveSlider();
      activeSlider?.flickityInstance?.next();
      return;
    }

    const minusBtn = target.closest('.qty-minus');
    const plusBtn = target.closest('.qty-plus');

    if (minusBtn || plusBtn) {
      const qtyWrapper = target.closest('.upsell-card__quantity');
      const input = qtyWrapper?.querySelector('.qty-input');
      if (!input) return;

      const parsedValue = Number.parseInt(input.value, 10);
      let val = Number.isNaN(parsedValue) ? 1 : parsedValue;

      if (minusBtn) {
        val = Math.max(1, val - 1);
      } else {
        val += 1;
      }

      input.value = val;
      return;
    }

    const addBtn = target.closest('.js-upsell-add');
    if (addBtn) {
      this.handleAddToCart(addBtn);
    }
  }

  handleTabKeydown(event, tab) {
    const { key } = event;
    const tabs = Array.from(this.tabs);
    const currentIndex = tabs.indexOf(tab);

    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    if (key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;

    this.switchTab(nextTab.dataset.tab, true);
  }

  switchTab(target, shouldFocus = false) {
    this.hideAllNavigation();

    let activeTab = null;
    this.tabs.forEach((t) => {
      const isActive = t.dataset.tab === target;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      t.setAttribute('tabindex', isActive ? '0' : '-1');

      if (isActive) {
        activeTab = t;
      }
    });

    this.contents.forEach((c) => {
      const isActive = c.dataset.tabContent === target;
      c.classList.toggle('is-active', isActive);
      c.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        this.initSlider(c);
      }
    });

    if (shouldFocus && activeTab) {
      activeTab.focus();
    }
  }

  initSlider(container) {
    const slider = container.querySelector('.product-cross-sell__slider');
    if (!slider) return;

    const region = slider.closest('.product-cross-sell__slider-region');
    region?.classList.remove('is-nav-visible');

    if (slider.classList.contains('flickity-enabled')) {
      if (slider.flickityInstance) {
        slider.flickityInstance.reloadCells();
        this.scheduleNavigationUpdate(slider.flickityInstance);
      }
      return;
    }

    const slides = slider.querySelectorAll('.product-cross-sell__slide');
    if (!slides.length) return;

    const flkty = new window.Flickity(slider, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      groupCells: true,
      dragThreshold: 10,
      adaptiveHeight: false,
      imagesLoaded: true,
    });

    slider.flickityInstance = flkty;
    slider.classList.remove('flickity-viewport-off');

    flkty.on('select', () => {
      this.updateNavigation(flkty, {
        reveal: this.isDesktopNav() || this.isNavRevealed(flkty),
      });
    });

    flkty.on('settle', () => {
      this.updateNavigation(flkty, {
        reveal: this.isDesktopNav() || this.isNavRevealed(flkty),
      });
    });

    this.scheduleNavigationUpdate(flkty);
  }

  equalizeSlideHeights(slider) {
    const slides = Array.from(slider.querySelectorAll('.product-cross-sell__slide'));
    if (!slides.length) return;

    slides.forEach((s) => (s.style.minHeight = ''));
    const maxHeight = Math.max(...slides.map((s) => s.offsetHeight));
    if (maxHeight <= 0) return;

    slides.forEach((s) => (s.style.minHeight = `${maxHeight}px`));
  }

  handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const cardInfo = button.closest('.upsell-card__info');
    const quantityInput = cardInfo ? cardInfo.querySelector('.qty-input') : null;
    const parsedQty = Number.parseInt(quantityInput ? quantityInput.value : '1', 10);
    const quantity = Number.isNaN(parsedQty) ? 1 : parsedQty;
    const labelDefault = button.dataset.labelDefault || button.textContent;
    const labelAdding = button.dataset.labelAdding || labelDefault;
    const labelAdded = button.dataset.labelAdded || labelDefault;
    const labelError = button.dataset.labelError || labelDefault;

    button.disabled = true;
    button.textContent = labelAdding;

    const formData = {
      items: [
        {
          id: variantId,
          quantity,
        },
      ],
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((data) => {
        button.textContent = labelAdded;

        document.dispatchEvent(new CustomEvent('cart:build'));
        fetch(window.Shopify.routes.root + 'cart.js')
          .then((res) => res.json())
          .then((cart) => {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
            document.dispatchEvent(new CustomEvent('cart:open'));
          });

        setTimeout(() => {
          button.disabled = false;
          button.textContent = labelDefault;
        }, 2000);
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        button.textContent = labelError;
        setTimeout(() => {
          button.disabled = false;
          button.textContent = labelDefault;
        }, 2000);
      });
  }
}

if (!customElements.get('product-cross-sell')) {
  customElements.define('product-cross-sell', ProductCrossSell);
}
